/**
 * TPL Product Carousel
 * Horizontal scroll carousel with multiple data sources:
 *   - specific collection / all / artist (server-rendered)
 *   - recommendations related/complementary (Shopify API, async swap)
 *   - recently viewed (client-side, localStorage + product .js fetch)
 */
(function () {
  'use strict';

  var RECENT_KEY = 'tpl:recently-viewed';
  var RECENT_MAX = 12;

  /* ───────── Recently-viewed tracker (runs on every PDP) ───────── */

  function currentProductHandle() {
    var m = window.location.pathname.match(/^\/products\/([^?#\/]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function trackPdpView() {
    var handle = currentProductHandle();
    if (!handle) return;
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) list = [];
      list = list.filter(function (h) { return h !== handle; });
      list.unshift(handle);
      if (list.length > RECENT_MAX) list.length = RECENT_MAX;
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) { /* localStorage unavailable */ }
  }

  /* ───────── Recently-viewed hydration ───────── */

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function moneyFromCents(cents) {
    var rupees = Math.round((cents || 0) / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  function imageWithWidth(src, width) {
    if (!src) return '';
    return src.replace(/(\.(?:jpg|jpeg|png|webp|gif))(\?.*)?$/i, '_' + width + 'x$1$2');
  }

  function renderRecentCard(p, settings) {
    if (!p || !p.handle) return '';
    var img = p.featured_image || (p.images && p.images[0]) || '';
    var altImg = (p.images && p.images[1] && p.images[1] !== img) ? p.images[1] : '';
    var src = imageWithWidth(img, 600);
    var srcset = [
      imageWithWidth(img, 300) + ' 300w',
      imageWithWidth(img, 400) + ' 400w',
      imageWithWidth(img, 600) + ' 600w'
    ].join(', ');
    var sizes = '(max-width: 749px) 210px, clamp(220px, 25vw, 320px)';

    var badge = '';
    if (!p.available) {
      badge = '<span class="tpl-carousel__badge tpl-carousel__badge--sold-out">' + escapeHtml(settings.soldOut) + '</span>';
    } else if ((p.tags || []).indexOf('limited-edition') !== -1) {
      badge = '<span class="tpl-carousel__badge tpl-carousel__badge--limited">' + escapeHtml(settings.limited) + '</span>';
    }

    var primary = src
      ? '<img class="tpl-carousel__image tpl-carousel__image--primary" src="' + escapeHtml(src) + '" srcset="' + escapeHtml(srcset) + '" sizes="' + escapeHtml(sizes) + '" loading="lazy" alt="' + escapeHtml(p.title) + '">'
      : '<div class="tpl-carousel__placeholder-img"></div>';

    var secondary = '';
    if (altImg) {
      var altSrc = imageWithWidth(altImg, 600);
      var altSrcset = [
        imageWithWidth(altImg, 300) + ' 300w',
        imageWithWidth(altImg, 400) + ' 400w',
        imageWithWidth(altImg, 600) + ' 600w'
      ].join(', ');
      secondary = '<img class="tpl-carousel__image tpl-carousel__image--secondary" src="' + escapeHtml(altSrc) + '" srcset="' + escapeHtml(altSrcset) + '" sizes="' + escapeHtml(sizes) + '" loading="lazy" alt="" aria-hidden="true">';
    }

    var imageBlock = primary + secondary;

    return [
      '<a href="', escapeHtml(p.url), '" class="tpl-carousel__card" data-carousel-card>',
        '<div class="tpl-carousel__image-wrap">',
          imageBlock,
          badge,
        '</div>',
        '<div class="tpl-carousel__card-info">',
          '<span class="tpl-carousel__product-title">', escapeHtml(p.title), '</span>',
          '<span class="tpl-carousel__price"><span class="tpl-carousel__price-from">', escapeHtml(settings.pricePrefix), '</span> ', moneyFromCents(p.price), '</span>',
        '</div>',
      '</a>'
    ].join('');
  }

  function hydrateRecentlyViewed(sectionEl) {
    var trackEl = sectionEl.querySelector('[data-carousel-track][data-recently-viewed]');
    if (!trackEl) return;

    var currentHandle = trackEl.dataset.currentHandle || currentProductHandle();
    var limit = parseInt(trackEl.dataset.limit, 10) || RECENT_MAX;
    var list;
    try {
      list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) { list = []; }
    if (!Array.isArray(list)) list = [];

    list = list.filter(function (h) { return h && h !== currentHandle; }).slice(0, limit);

    if (!list.length) {
      sectionEl.remove();
      return;
    }

    var settings = {
      soldOut: sectionEl.dataset.labelSoldOut || 'Sold Out',
      limited: sectionEl.dataset.labelLimited || 'Limited Edition',
      pricePrefix: sectionEl.dataset.labelPricePrefix || 'from'
    };

    Promise.all(list.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var html = products
        .filter(function (p) { return p && p.handle && (!p.tags || p.tags.indexOf('addon') === -1); })
        .map(function (p) { return renderRecentCard(p, settings); })
        .join('');

      if (!html) {
        sectionEl.remove();
        return;
      }
      trackEl.innerHTML = html;
      sectionEl.removeAttribute('hidden');
    });
  }

  /* ───────── Carousel scroll/arrow logic ───────── */

  function setupCarousel(sectionEl) {
    if (sectionEl.dataset.carouselInit) return;
    sectionEl.dataset.carouselInit = '1';

    function getTrack() { return sectionEl.querySelector('[data-carousel-track]'); }
    function getBar() { return sectionEl.querySelector('[data-carousel-progress]'); }
    function getPrev() { return sectionEl.querySelector('[data-carousel-prev]'); }
    function getNext() { return sectionEl.querySelector('[data-carousel-next]'); }

    function getScrollAmount() {
      var track = getTrack();
      var card = track && track.querySelector('[data-carousel-card]');
      return card ? (card.offsetWidth + 20) * 2 : 400;
    }

    /* Perf 2026-10-03 (MotionScore: thrashing D, 18 interleaved layout
       reads/writes per frame). The old path wrote the progress bar, then read
       the track, then read+wrote every card in turn. One rAF now does ALL
       reads first (track rect, scroll metrics, every card rect) and ALL
       writes after. Nothing paints while the section is off-screen. */
    var coverflow = sectionEl.classList.contains('tpl-carousel--coverflow') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var ticking = false;
    var onScreen = true;   /* IntersectionObserver flips this when supported */
    var dirty = false;     /* a paint was requested while off-screen */

    function readProgress(track) {
      return { scrollLeft: track.scrollLeft, range: track.scrollWidth - track.clientWidth };
    }
    function writeProgress(m) {
      var bar = getBar(), pBtn = getPrev(), nBtn = getNext();
      if (!bar) return;
      if (m.range <= 0) {
        bar.style.width = '100%';
        if (pBtn) pBtn.disabled = true;
        if (nBtn) nBtn.disabled = true;
        return;
      }
      bar.style.width = (m.scrollLeft / m.range) * 100 + '%';
      if (pBtn) pBtn.disabled = m.scrollLeft <= 2;
      if (nBtn) nBtn.disabled = m.scrollLeft >= m.range - 2;
    }

    /* Coverflow (motion.dev port, 2026-09-07): each card tilts, shrinks and
       recedes by its distance from the track centre. Focal point: viewport
       centre (desktop) or the leading card's resting position at the page
       gutter (phones, --cf-focal: start). */
    function readCoverflow(track) {
      var tr = track.getBoundingClientRect();
      var cs = window.getComputedStyle(sectionEl);
      var focalMode = (cs.getPropertyValue('--cf-focal') || 'center').trim();
      var gutter = parseFloat(cs.getPropertyValue('--cf-gutter')) || 16;
      var cards = track.querySelectorAll('[data-carousel-card]');
      var rects = new Array(cards.length);
      var firstIdx = -1;
      for (var i = 0; i < cards.length; i++) {
        rects[i] = cards[i].getBoundingClientRect();
        if (firstIdx < 0 && !cards[i].dataset.cfClone) firstIdx = i;
      }
      if (firstIdx < 0 && cards.length) firstIdx = 0;
      var mid = focalMode === 'start' && firstIdx >= 0
        ? tr.left + gutter + rects[firstIdx].width / 2
        : tr.left + track.clientWidth / 2;
      return {
        cards: cards, rects: rects, mid: mid,
        tilt: parseFloat(cs.getPropertyValue('--cf-tilt')) || 34,
        shrink: parseFloat(cs.getPropertyValue('--cf-scale')) || 0.1,
        depth: parseFloat(cs.getPropertyValue('--cf-depth')) || 50
      };
    }
    function writeCoverflow(m) {
      for (var i = 0; i < m.cards.length; i++) {
        var c = m.cards[i], cr = m.rects[i];
        var d = (cr.left + cr.width / 2 - m.mid) / (cr.width * 1.15);
        var cl = Math.max(-1.6, Math.min(1.6, d));
        var a = Math.abs(cl);
        c.style.transform = 'rotateY(' + (-cl * m.tilt).toFixed(2) + 'deg) scale(' + (1 - a * m.shrink).toFixed(3) + ') translateZ(' + (-a * m.depth).toFixed(1) + 'px)';
        c.style.opacity = (1 - a * 0.22).toFixed(3);
        c.style.zIndex = String(10 - Math.round(a * 4));
      }
    }

    function paint() {
      ticking = false;
      var track = getTrack();
      if (!track) return;
      if (!onScreen) { dirty = true; return; }
      dirty = false;
      /* READ phase */
      var prog = readProgress(track);
      var cf = coverflow ? readCoverflow(track) : null;
      /* WRITE phase */
      writeProgress(prog);
      if (cf) writeCoverflow(cf);
    }
    function requestPaint() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    }

    sectionEl.addEventListener('click', function (e) {
      var prev = e.target.closest && e.target.closest('[data-carousel-prev]');
      var next = e.target.closest && e.target.closest('[data-carousel-next]');
      if (!prev && !next) return;
      var track = getTrack();
      if (!track) return;
      var motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var amount = getScrollAmount();
      track.scrollBy({ left: prev ? -amount : amount, behavior: motionOk ? 'smooth' : 'auto' });
    });

    /* Jump the track so `card` sits at its resting start. Measures first,
       then writes — the old version set scrollBehavior before measuring,
       which forced a layout on every init. */
    function jumpTo(track, card) {
      var start = card.getBoundingClientRect().left - track.getBoundingClientRect().left +
        track.scrollLeft - (parseFloat(window.getComputedStyle(track).paddingLeft) || 0);
      var prevBehavior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = start;
      track.style.scrollBehavior = prevBehavior;
    }
    /* Centred start with neighbours (owner 2026-09-08, the version they liked):
       the set of cards is cloned once on each side so the view opens on the
       first real card at the focal point with cards on both sides. Scrolling is
       fully native, no jumping; the track simply ends after the third set.
       Clones are aria-hidden and unfocusable. */
    function setupNeighbours(track) {
      if (!coverflow || !track || track.dataset.cfLoop === '1') return;
      var originals = Array.prototype.filter.call(track.querySelectorAll('[data-carousel-card]'), function (c) { return !c.dataset.cfClone; });
      if (originals.length < 3) return;
      track.dataset.cfLoop = '1';
      function clone(c) {
        var k = c.cloneNode(true);
        k.dataset.cfClone = '1';
        k.setAttribute('aria-hidden', 'true');
        k.setAttribute('tabindex', '-1');
        k.querySelectorAll('a, button, [tabindex]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
        return k;
      }
      var before = document.createDocumentFragment(), after = document.createDocumentFragment();
      originals.forEach(function (c) { before.appendChild(clone(c)); after.appendChild(clone(c)); });
      track.insertBefore(before, track.firstChild);
      track.appendChild(after);
      jumpTo(track, originals[0]);
      requestPaint();
    }

    var lastTrack = null;
    function attachScroll() {
      var track = getTrack();
      if (!track || track === lastTrack) return;
      lastTrack = track;
      track.addEventListener('scroll', requestPaint, { passive: true });
      if (coverflow) {
        setupNeighbours(track);
        setTimeout(function () {
          var f = track.querySelector('[data-carousel-card]:not([data-cf-clone])');
          if (f && track.dataset.cfLoop === '1') { jumpTo(track, f); requestPaint(); }
        }, 400);
      }
      requestPaint();
    }

    /* Entry reveal for the first six cards — only once the section is on
       screen (it used to fire at init, 54 off-screen animations on the home). */
    var revealed = false;
    function revealCards() {
      if (revealed || !onScreen || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var track = getTrack();
      if (!track || typeof Element.prototype.animate !== 'function') return;
      var cards = track.querySelectorAll('[data-carousel-card]:not([data-cf-clone])');
      if (!cards.length) return;
      revealed = true;
      for (var i = 0; i < cards.length && i < 6; i++) {
        cards[i].animate([{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 450, delay: i * 70, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'backwards', composite: 'add' });
      }
    }

    attachScroll();
    window.addEventListener('resize', requestPaint, { passive: true });
    if ('IntersectionObserver' in window) {
      onScreen = false;
      new IntersectionObserver(function (entries) {
        onScreen = entries.some(function (en) { return en.isIntersecting; });
        if (!onScreen) return;
        revealCards();
        requestPaint();
        if (coverflow) setTimeout(requestPaint, 300);
      }, { threshold: 0.05, rootMargin: '120px 0px' }).observe(sectionEl);
    }
    if (coverflow) {
      /* Repaint once layout has settled (images sizing the cards, fonts, late
         load) and whenever the track changes width. */
      var t0 = getTrack();
      if (t0) { setTimeout(requestPaint, 60); setTimeout(requestPaint, 400); setTimeout(requestPaint, 1200); }
      window.addEventListener('load', requestPaint);
      sectionEl.addEventListener('load', function (e) { if (e.target && e.target.tagName === 'IMG') requestPaint(); }, true);
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(function () { requestPaint(); });
        ro.observe(sectionEl);
        if (t0) ro.observe(t0);
      }
    }

    var mo = new MutationObserver(function () {
      attachScroll();
      requestPaint();
      revealCards();
    });
    mo.observe(sectionEl, { childList: true, subtree: true });
  }

  function initSection(sectionEl) {
    if (!sectionEl) return;
    setupCarousel(sectionEl);
    if (sectionEl.dataset.carouselSource === 'recent') {
      hydrateRecentlyViewed(sectionEl);
    }
  }

  /* ───────── Boot ───────── */

  function boot() {
    trackPdpView();
    document.querySelectorAll('.tpl-carousel').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('.tpl-carousel');
    if (section) initSection(section);
  });
})();
